import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { JsonView } from "@/components/json-view";
import { Panel } from "@/components/panel";
import { StatCard } from "@/components/stat-card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { apiGet, apiPost } from "@/lib/api-client";
import { asRecord } from "@/lib/type-utils";

const ROLES = ["specialist", "coder", "qa", "security", "librarian", "architect", "scout"] as const;
const CATEGORIES = ["politics_us", "politics_intl", "crypto", "sports", "economics", "science", "legal"] as const;

const ROLE_COLORS: Record<string, string> = {
  specialist: "text-blue-400", coder: "text-cyan-400", qa: "text-orange-400",
  security: "text-red-400", librarian: "text-emerald-400", architect: "text-gold-400", scout: "text-violet-400",
};

interface EvolutionResult {
  role: string;
  category: string;
  champion_brier: number | null;
  best_mutant_brier: number | null;
  improvement_pct: number;
  promoted: boolean;
  new_champion_id: string | null;
  mutants_tested: number;
  markets_used: number;
  total_cost_usd: number;
  validation_passed: boolean;
}

interface HistoryEntry {
  event_type: string;
  prompt_id: string;
  agent_role: string;
  category: string;
  generation: number;
  brier_score: number | null;
  markets_tested: number | null;
  champion_brier: number | null;
  improvement_pct: number;
  created_at: string;
}

interface LeaderboardEntry {
  prompt_id: string;
  agent_role: string;
  category: string;
  generation: number;
  total_uses: number;
  avg_brier_score: number | null;
  recent_avg_brier: number | null;
  is_champion: boolean;
  mutation_strategy: string | null;
  created_at: string;
}

const EVENT_BADGES: Record<string, { bg: string; text: string }> = {
  mutated: { bg: "bg-blue-500/20", text: "text-blue-400" },
  backtested: { bg: "bg-yellow-500/20", text: "text-yellow-400" },
  promoted: { bg: "bg-green-500/20", text: "text-green-400" },
  retired: { bg: "bg-red-500/20", text: "text-red-400" },
  validated: { bg: "bg-purple-500/20", text: "text-purple-400" },
};

export function PromptEvolutionPage() {
  const [selectedRole, setSelectedRole] = useState<string>("specialist");
  const [selectedCategory, setSelectedCategory] = useState<string>("politics_us");
  const [lastResult, setLastResult] = useState<EvolutionResult | null>(null);

  const statsQuery = useQuery({
    queryKey: ["prompt-evolution-stats"],
    queryFn: () => apiGet<{ success: boolean; data: Record<string, unknown> }>("/api/prompts/stats"),
  });

  const leaderboardQuery = useQuery({
    queryKey: ["prompt-evolution-leaderboard"],
    queryFn: () => apiGet<{ success: boolean; data: LeaderboardEntry[] }>("/api/prompts/variants"),
  });

  const historyQuery = useQuery({
    queryKey: ["prompt-evolution-history"],
    queryFn: () => apiGet<{ success: boolean; data: HistoryEntry[] }>("/api/prompts/history?limit=30"),
  });

  const evolveMutation = useMutation({
    mutationFn: async () => {
      const resp = await apiPost<{ success: boolean; data: EvolutionResult }>("/api/prompts/evolve", {
        role: selectedRole, category: selectedCategory,
      });
      return asRecord(resp).data as EvolutionResult;
    },
    onSuccess: (data) => {
      setLastResult(data);
      leaderboardQuery.refetch();
      historyQuery.refetch();
      statsQuery.refetch();
    },
  });

  const [evolveAllResults, setEvolveAllResults] = useState<Record<string, unknown> | null>(null);
  // Evolve All fires 7 parallel LLM evolution cycles (~$15-25 per run, backend
  // /evolve-all docstring) — a one-click spend near the $20 rail, so it is
  // two-step armed like the remote kill button.
  const [evolveAllConfirm, setEvolveAllConfirm] = useState(false);

  const evolveAllMutation = useMutation({
    mutationFn: async () => {
      const resp = await apiPost<unknown>("/api/prompts/evolve-all", {
        category: selectedCategory,
      });
      return asRecord(resp);
    },
    onSuccess: (data) => {
      setEvolveAllResults(data as Record<string, unknown>);
      leaderboardQuery.refetch();
      historyQuery.refetch();
      statsQuery.refetch();
    },
  });

  // Confirm-window timer is tracked so (a) rapid clicks don't queue stacked
  // timeouts that cut the window short and (b) unmount cancels it instead of
  // setting state on a dead component (Gemini/Greptile, PR #1896).
  const evolveAllTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearEvolveAllTimer = () => {
    if (evolveAllTimerRef.current !== null) {
      clearTimeout(evolveAllTimerRef.current);
      evolveAllTimerRef.current = null;
    }
  };
  useEffect(() => clearEvolveAllTimer, []);

  const handleEvolveAll = () => {
    if (!evolveAllConfirm) {
      clearEvolveAllTimer();
      setEvolveAllConfirm(true);
      evolveAllTimerRef.current = setTimeout(() => {
        evolveAllTimerRef.current = null;
        setEvolveAllConfirm(false);
      }, 6000);
      return;
    }
    clearEvolveAllTimer();
    setEvolveAllConfirm(false);
    evolveAllMutation.mutate();
  };

  const stats = statsQuery.data ? asRecord(asRecord(statsQuery.data).data) : null;
  const leaderboard = leaderboardQuery.data ? (asRecord(leaderboardQuery.data).data as LeaderboardEntry[]) : [];
  const history = historyQuery.data ? (asRecord(historyQuery.data).data as HistoryEntry[]) : [];

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="Total Variants" value={String(stats.total_variants ?? 0)} />
          <StatCard label="Active Champions" value={String(stats.active_champions ?? 0)} />
          <StatCard label="Promotions" value={String(stats.total_promotions ?? 0)} tone="success" />
          <StatCard label="Avg Improvement" value={`${stats.avg_improvement_pct ?? 0}%`} />
        </div>
      )}

      {/* Evolve Controls */}
      <Panel title="Prompt Evolution" description="Mutate, backtest, and promote prompts using production models"
        actions={
          <div className="flex gap-2">
            <Button
              onClick={() => evolveMutation.mutate()}
              disabled={evolveMutation.isPending || evolveAllMutation.isPending}>
              {evolveMutation.isPending ? "Evolving..." : "Evolve Role"}
            </Button>
            <button type="button"
              className={`rounded-lg border px-4 py-2 text-sm font-medium transition-all disabled:opacity-50 ${
                evolveAllConfirm
                  ? "animate-pulse border-amber-500 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20"
                  : "border-purple-500 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20"
              }`}
              onClick={handleEvolveAll}
              disabled={evolveMutation.isPending || evolveAllMutation.isPending}>
              {evolveAllMutation.isPending
                ? "Evolving 7 agents..."
                : evolveAllConfirm
                  ? "Confirm ~$15-25 LLM spend"
                  : "Evolve All 7 (~$15-25)"}
            </button>
          </div>
        }>
        <div className="flex flex-wrap gap-4">
          <div>
            <Label>Agent Role</Label>
            <Select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} title="Select agent role to evolve">
              {ROLES.map((r) => (
                <option key={r} value={r}>{r.toUpperCase()}</option>
              ))}
            </Select>
          </div>
          <div>
            <Label>Category</Label>
            <Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} title="Select market category">
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c.replace("_", " ").toUpperCase()}</option>
              ))}
            </Select>
          </div>
        </div>

        <div className="mt-4 rounded-lg bg-gunmetal/50 p-3 text-xs text-slate-400">
          <div className="font-medium text-slate-300">What happens when you click Evolve:</div>
          <ol className="mt-1 list-inside list-decimal space-y-0.5">
            <li>Generate 3 mutant prompts via meta-prompting (Claude Opus)</li>
            <li>Backtest each mutant against 20 resolved markets (production model)</li>
            <li>If best mutant beats champion by 5%+ Brier improvement, validate in full pipeline</li>
            <li>Promote if validation passes</li>
          </ol>
          <div className="mt-1">Estimated cost: ~$2-4 per single-role cycle. Estimated time: ~2-5 minutes.</div>
          <div className="mt-1 text-amber-400/90">
            Evolve All 7 runs every role at once: ~$15-25 LLM spend per run (7 × $2-4) — it asks for a second tap to confirm.
          </div>
        </div>
      </Panel>

      {/* Errors */}
      {evolveMutation.isError && (
        <Panel title="Evolution Error">
          <div className="text-red-400 text-sm">
            {evolveMutation.error instanceof Error ? evolveMutation.error.message : "Evolution failed."}
          </div>
        </Panel>
      )}
      {evolveAllMutation.isError && (
        <Panel title="Evolve All Error">
          <div className="text-red-400 text-sm">
            {evolveAllMutation.error instanceof Error ? evolveAllMutation.error.message : "Evolve All failed."}
          </div>
        </Panel>
      )}

      {/* Evolve All Results */}
      {evolveAllResults && (
        <Panel title="Evolve All Results" description={`All 7 agents × ${selectedCategory}`}>
          {(() => {
            const summary = asRecord(evolveAllResults).summary as Record<string, unknown> | undefined;
            const results = (asRecord(evolveAllResults).results ?? []) as Record<string, unknown>[];
            return (
              <>
                {summary && (
                  <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <StatCard label="Roles Evolved" value={String(summary.roles_evolved ?? 0)} />
                    <StatCard label="Succeeded" value={String(summary.roles_succeeded ?? 0)} />
                    <StatCard label="Promoted" value={String(summary.roles_promoted ?? 0)} tone="success" />
                    <StatCard label="Total Cost" value={`$${Number(summary.total_cost_usd ?? 0).toFixed(2)}`} />
                  </div>
                )}
                <div className="space-y-2">
                  {results.map((r, i) => {
                    const data = asRecord(r.data ?? {});
                    const role = String(r.role ?? "unknown");
                    return (
                      <div key={i} className="flex items-center gap-3 rounded-lg bg-gunmetal/30 px-3 py-2 text-sm">
                        <span className={`w-24 font-semibold uppercase ${ROLE_COLORS[role] ?? "text-slate-300"}`}>{role}</span>
                        {r.success ? (
                          <>
                            <span className={`rounded px-1.5 py-0.5 text-xs ${data.promoted ? "bg-green-500/20 text-green-400" : "bg-slate-500/20 text-slate-400"}`}>
                              {data.promoted ? "PROMOTED" : "NO CHANGE"}
                            </span>
                            <span className="text-xs text-slate-500">
                              Brier: {data.champion_brier != null ? Number(data.champion_brier).toFixed(4) : "--"}
                              {data.best_mutant_brier != null && ` → ${Number(data.best_mutant_brier).toFixed(4)}`}
                            </span>
                            <span className="text-xs text-slate-600">${Number(data.total_cost_usd ?? 0).toFixed(3)}</span>
                          </>
                        ) : (
                          <span className="text-xs text-red-400">{String(r.error ?? "failed")}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </Panel>
      )}

      {/* Last Result */}
      {lastResult && (
        <Panel title="Evolution Result" description={`${lastResult.role.toUpperCase()} / ${lastResult.category}`}>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="rounded-lg bg-gunmetal/50 p-3 text-center">
              <div className="text-lg font-bold text-gold-400">
                {lastResult.champion_brier != null ? lastResult.champion_brier.toFixed(4) : "--"}
              </div>
              <div className="text-xs text-slate-400">Champion Brier</div>
            </div>
            <div className="rounded-lg bg-gunmetal/50 p-3 text-center">
              <div className="text-lg font-bold text-gold-400">
                {lastResult.best_mutant_brier != null ? lastResult.best_mutant_brier.toFixed(4) : "--"}
              </div>
              <div className="text-xs text-slate-400">Best Mutant Brier</div>
            </div>
            <div className="rounded-lg bg-gunmetal/50 p-3 text-center">
              <div className={`text-lg font-bold ${lastResult.improvement_pct > 0.05 ? "text-green-400" : "text-slate-300"}`}>
                {(lastResult.improvement_pct * 100).toFixed(1)}%
              </div>
              <div className="text-xs text-slate-400">Improvement</div>
            </div>
            <div className="rounded-lg bg-gunmetal/50 p-3 text-center">
              <div className={`text-lg font-bold ${lastResult.promoted ? "text-green-400" : "text-slate-400"}`}>
                {lastResult.promoted ? "PROMOTED" : "NO CHANGE"}
              </div>
              <div className="text-xs text-slate-400">Status</div>
            </div>
          </div>
          <div className="mt-3 flex gap-4 text-xs text-slate-500">
            <span>Mutants tested: {lastResult.mutants_tested}</span>
            <span>Markets used: {lastResult.markets_used}</span>
            <span>Cost: ${lastResult.total_cost_usd.toFixed(3)}</span>
            <span>Validation: {lastResult.validation_passed ? "passed" : "skipped/failed"}</span>
          </div>
        </Panel>
      )}

      {/* Leaderboard */}
      <Panel title="Prompt Leaderboard" description="Active variants ranked by Brier score (lower is better)">
        {leaderboard.length === 0 ? (
          <div className="text-sm text-slate-400">No prompt variants yet. Run an evolution cycle to get started.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Gen</TableHead>
                <TableHead>Brier</TableHead>
                <TableHead>Uses</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Strategy</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leaderboard.map((entry, i) => (
                <TableRow key={i}>
                  <TableCell className={`font-medium uppercase ${ROLE_COLORS[entry.agent_role] ?? "text-slate-300"}`}>
                    {entry.agent_role}
                  </TableCell>
                  <TableCell className="text-slate-300">{entry.category}</TableCell>
                  <TableCell className="text-slate-400">G{entry.generation}</TableCell>
                  <TableCell className="font-mono text-gold-400">
                    {entry.avg_brier_score != null ? entry.avg_brier_score.toFixed(4) : "--"}
                  </TableCell>
                  <TableCell className="text-slate-400">{entry.total_uses}</TableCell>
                  <TableCell>
                    {entry.is_champion ? (
                      <span className="rounded bg-green-500/20 px-1.5 py-0.5 text-xs text-green-400">CHAMPION</span>
                    ) : (
                      <span className="text-xs text-slate-500">variant</span>
                    )}
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate text-slate-500">{entry.mutation_strategy ?? "--"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      {/* History Timeline */}
      <Panel title="Evolution History" description="Recent mutations, backtests, promotions, and retirements">
        {history.length === 0 ? (
          <div className="text-sm text-slate-400">No evolution events yet.</div>
        ) : (
          <div className="space-y-2">
            {history.map((event, i) => {
              const badge = EVENT_BADGES[event.event_type] ?? { bg: "bg-slate-500/20", text: "text-slate-400" };
              return (
                <div key={i} className="flex items-center gap-3 rounded-lg bg-gunmetal/30 px-3 py-2 text-sm">
                  <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${badge.bg} ${badge.text}`}>
                    {event.event_type.toUpperCase()}
                  </span>
                  <span className={`uppercase ${ROLE_COLORS[event.agent_role] ?? "text-slate-300"}`}>
                    {event.agent_role}
                  </span>
                  <span className="text-slate-400">{event.category}</span>
                  <span className="text-xs text-slate-500">G{event.generation}</span>
                  {event.brier_score != null && (
                    <span className="font-mono text-xs text-gold-400">Brier: {event.brier_score.toFixed(4)}</span>
                  )}
                  {event.improvement_pct > 0 && (
                    <span className="text-xs text-green-400">+{event.improvement_pct.toFixed(1)}%</span>
                  )}
                  <span className="ml-auto text-xs text-slate-600">
                    {event.created_at ? new Date(event.created_at).toLocaleString() : ""}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      {/* Raw Stats */}
      <Panel title="Raw Stats"><JsonView value={statsQuery.data} /></Panel>
    </div>
  );
}
